import clsx from 'clsx';
import { forwardRef, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import SearchableSelect from './SearchableSelect';

const base = 'w-full px-3 py-2 text-sm rounded-lg bg-white border border-surface-200 text-dark-900 placeholder-surface-400 transition-all duration-200 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 hover:border-surface-300 disabled:bg-surface-50 disabled:text-surface-400 disabled:cursor-not-allowed';

export const Input = forwardRef(function Input({ className, error, variant = 'light', ...props }, ref) {
  const isDark = variant === 'dark';
  return (
    <input
      ref={ref}
      className={clsx(
        isDark ? 'input-field' : base,
        error && (isDark ? 'input-error' : 'border-error-400 focus:border-error-500 focus:ring-error-500/10'),
        className,
      )}
      {...props}
    />
  );
});

export const PasswordInput = forwardRef(function PasswordInput({ className, error, variant = 'light', ...props }, ref) {
  const [visible, setVisible] = useState(false);
  const isDark = variant === 'dark';

  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={clsx(
          isDark ? 'input-field pr-10' : base,
          !isDark && 'pr-10',
          error && (isDark ? 'input-error' : 'border-error-400 focus:border-error-500 focus:ring-error-500/10'),
          className,
        )}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className={clsx(
          'absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 transition-colors',
          isDark ? 'text-surface-400 hover:text-white' : 'text-surface-400 hover:text-surface-600',
        )}
        aria-label={visible ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {visible ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
      </button>
    </div>
  );
});

export const Textarea = forwardRef(function Textarea({ className, error, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={clsx(base, 'resize-none min-h-[72px]', error && 'border-error-400 focus:border-error-500 focus:ring-error-500/10', className)}
      {...props}
    />
  );
});

export const Select = forwardRef(function Select({
  className,
  error,
  children,
  options,
  placeholder,
  searchPlaceholder,
  placeholderValues,
  ...props
}, ref) {
  return (
    <SearchableSelect
      ref={ref}
      className={className}
      error={error}
      options={options}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      placeholderValues={placeholderValues}
      {...props}
    >
      {children}
    </SearchableSelect>
  );
});

export { default as SearchableSelect, DROPDOWN_SEARCH_INPUT_CLASS, SEARCHABLE_SELECT_TRIGGER_CLASS } from './SearchableSelect';
export { default as SearchableMultiSelect } from './SearchableMultiSelect';

export function Field({ label, hint, error, required, children, className, variant = 'light' }) {
  const labelClass =
    variant === 'auth'
      ? 'text-surface-200'
      : variant === 'dark'
        ? 'text-surface-300'
        : 'text-surface-600';
  const isDark = variant === 'dark';

  return (
    <div className={className}>
      {label && (
        <label className={clsx('block text-xs font-medium mb-1.5', labelClass)}>
          {label}
          {required && <span className="text-error-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className={clsx('text-[13px] mt-0.5', isDark ? 'text-error-400' : 'text-error-600')}>{error}</p>}
      {hint && !error && <p className="text-[13px] text-surface-400 mt-0.5">{hint}</p>}
    </div>
  );
}
